/**
 * ProgressiveBlurTop
 *
 * A smooth, progressive blur pinned to the top edge of the viewport — the same
 * effect seen on outloud.co. On the web this is a pure-CSS technique: a stack of
 * absolutely-positioned layers, each with its own `backdrop-filter: blur()` and a
 * `mask-image` linear-gradient that reveals it only from a rising threshold up to
 * the top. Because each layer samples the backdrop already painted behind it
 * (including the prior, lighter layers), the blur compounds — yielding zero blur
 * at the bottom edge ramping smoothly to maximum blur at the very top.
 *
 * Sits fixed above page content but below the navbar, and is fully click-through.
 */

// Blur radius per layer, weakest → strongest (bottom → top). Roughly doubling.
const BLUR_RADII = [0.1, 0.15, 0.3, 0.6, 1.25, 2.5, 5, 10]

type ProgressiveBlurTopProps = {
  /** Height of the blur band in px. Default 140. */
  height?: number
  /** Stacking order. Default 50 — above content (10), below navbar (100). */
  zIndex?: number
  /** Top offset in px. Default 0. */
  top?: number
}

export function ProgressiveBlurTop({ height = 140, zIndex = 50, top = 0 }: ProgressiveBlurTopProps) {
  const layerCount = BLUR_RADII.length
  const step = 100 / layerCount // 12.5% per layer

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top,
        left: 0,
        right: 0,
        height,
        zIndex,
        pointerEvents: 'none',
      }}
    >
      {BLUR_RADII.map((radius, i) => {
        // Each successive layer reveals from a threshold 12.5% higher than the
        // last, up to the top edge. `to top` => 0% is the bottom, 100% the top.
        // Layer 0 covers almost everything above 12.5% (lightest blur);
        // the final layer is confined to the top 12.5% (heaviest blur).
        const start = i * step
        const end = (i + 1) * step
        const mask = `linear-gradient(to top, transparent ${start}%, black ${end}%)`

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              inset: 0,
              backdropFilter: `blur(${radius}px)`,
              WebkitBackdropFilter: `blur(${radius}px)`,
              maskImage: mask,
              WebkitMaskImage: mask,
            }}
          />
        )
      })}
    </div>
  )
}

export default ProgressiveBlurTop
